import {
	ClientActivityTypeEnum,
	ClientBondInterestTypeEnum,
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientEventTypeEnum,
	ClientFinancialPlanExpenseNameEnum,
	ClientFinancialPlanExpenseTypeEnum,
	ClientIdentificationDocumentTypeEnum,
	ClientPortfolioDataCommodityServiceEnum,
	ClientPortfolioDomainEnum,
	ClientPortfolioInstructionHoldingTypeEnum,
	ClientPortfolioInsuredPersonRiskEnum,
	ClientPortfolioInvestmentEnterFeeAmountTypeEnum,
	ClientPortfolioInvestmentEnterFeeApplyingEnum,
	ClientPortfolioInvestmentEnterFeeTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTransactionEnum,
	ClientPortfolioInvestmentTypeEnum,
	ClientPortfolioPaymentFrequencyEnum,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
	ClientPropertyCarVehicleTypeEnum,
	ClientPropertyCategoryEnum,
	ClientPropertyOwnershipEnum,
	ClientPropertyRenovationEnum,
	ClientPropertyTypeEnum,
	ClientPropertyVehicleTypeEnum,
	CurrencyEnum,
	UserTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import englishZodTranslations from "zod-i18n-map/locales/en/zod.json";

export const MyPlannEnglishResourcesDictionary: MyPlannResourceDictionary = {
	Common: {
		AppName: "myPLANN but english",
		CurrencySign: "Kč",
		ValueNotProvided: "Value not provided",
		ValueNotProvidedSubtitle: "If you know the value, let your advisor know.",
		LoadMore: "Load older",
		save: "Save",
		continue: "Continue",
		ShowMore: "Show more",
		Send: "Send",
		Search: "Search",
		Filter: "Filter",
		Apply: "Apply",
		Name: "Name",
		FirstName: "First name",
		NoData: "No data",
		ToDate: "To date",
		Min: "Min",
		Max: "Max",
		solvedEvents: "Solving events",
		upcomingEvents: "Upcoming events",
		overdueEvents: "Overdue events",
		resolvedEvents: "Resolved events",
		DisplayAll: "Display all",
		Hide: "Hide",
		OneDocument: "document",
		FewDocuments: "documents",
		MoreDocuments: "documents",
		NoDocuments: "No documents",
		NotSpecified: "Not specified",
		ToolbarMenu: {
			WritteMessage: "Writte message",
			Call: "Call",
			WriteSMS: "Writte SMS",
			WriteEmail: "Writte E-mail",
		},
		somethingWentWrong: "Something went wrong, please contact your advisor",
		Cancel: "Cancel",
		Confirm: "Confirm",
	},
	Errors: {
		ApplicationError: {
			Home: "Home",
		},
		Error404: {
			Home: "Home",
			Subtitle: "Page not found",
		},
	},
	zod: englishZodTranslations,
	Profile: {
		title: "Profile",
		MissingAdvisor: {
			title: "You do not have an assigned advisor",
			description:
				"Your application has no source to load data from. Please contact your advisor.",
			logOut: "Log out",
		},
		Fields: {
			phone: "Phone",
			email: "E-mail",
		},
		Sections: {
			PersonalDataAndAccounts: "Personal data and accounts",
			Addresses: "Addresses",
			Paperwork: "Documents",
			FinancialBalance: "Financial balance",
		},
		EmptyList: {
			Addresses: {
				label: "No addresses",
				description: "Here you will see all of your addresses.",
			},
			Paperwork: {
				label: "No paperwork",
				description: "Here you will see all of your paperwork.",
			},
		},
	},
	PersonalDataAndAccounts: {
		title: "Personal data",
		PersonalData: {
			title: "Personal data",
			Name: "Name",
			Surname: "Surname",
			BirthNumber: "Birth number",
			BirthDate: "Birth date",
			BirthPlace: "Birth place",
			MaritalStatus: "Marital status",
			HealthInsurance: "Health insurance",
		},
	},
	Addresses: {
		title: "Addresses",
		Type: {
			permanent: "Permanent",
			contact: "Contact",
		},
		PermanentAddress: {
			title: "Permanent address",
			Street: "Street",
			StreetNumber: "Description and reference number",
			City: "City",
			Zip: "ZIP",
			Country: "Country",
		},
		ContactAddress: {
			title: "Contact address",
			Street: "Street",
			StreetNumber: "Desc. and ref. number",
			City: "City",
			Zip: "ZIP",
			Country: "Country",
		},
	},
	Paperwork: {
		title: "Documents",
		Types: {
			[ClientIdentificationDocumentTypeEnum.Passport]: "Passport",
			[ClientIdentificationDocumentTypeEnum.IdentityCard]: "Identity card",
			[ClientIdentificationDocumentTypeEnum.WeaponLicense]: "Weapon license",
			[ClientIdentificationDocumentTypeEnum.STK]: "STK",
			[ClientIdentificationDocumentTypeEnum.DrivingLicense]: "Driving license",
			[ClientIdentificationDocumentTypeEnum.FishingLicense]: "Fishing license",
			[ClientIdentificationDocumentTypeEnum.HuntingLicense]: "Hunting license",
			[ClientIdentificationDocumentTypeEnum.ISIC]: "ISIC",
			[ClientIdentificationDocumentTypeEnum.InsuranceCard]: "Insurance card",
			[ClientIdentificationDocumentTypeEnum.Other]: "Other",
			[ClientIdentificationDocumentTypeEnum.PilotLicense]: "Pilot license",
			[ClientIdentificationDocumentTypeEnum.ResidencePermit]:
				"Residence permit",
			[ClientIdentificationDocumentTypeEnum.ShipLicense]: "Ship license",
			[ClientIdentificationDocumentTypeEnum.TravelLicense]: "Travel license",
		},
		DocumentNumber: "Document number",
		ExpirationDate: "Expiration date",
	},
	Settings: {
		title: "More",
		DarkMode: "Dark mode",
		Language: "Language",
		LogOut: "Log out",
		RemoveAccount: "Remove account",
		AppVersion: "App version",
		LanguageOptions: {
			Czech: "Čeština",
			English: "English",
		},
		Sections: {
			Tools: "Tools",
			Settings: "Settings",
			Support: "Support",
		},
		Items: {
			FinancialBalance: "Financial balance",
			MyDocuments: "My documents",
			Activities: "Activities",
			Calculators: "Calculators",
			Settings: "Settings",
			Notifications: "Notifications",
			Security: "Security",
			HelpAndSupport: "Help and support",
		},
		Copyright: "© 2026 SAB servis s.r.o.",
	},
	Security: {
		title: "Security",
		Biometry: "Biometry",
		BiometryError: "Failed to change biometry settings",
		Sections: {
			Login: "Login",
		},
	},
	Dashboard: {
		TitleCard: {
			title: "Detail",
			tooltip:
				"The overview is based on data provided by the financial institution or advisor. If you want to verify the accuracy of the data, please contact your advisor.",
			zeroValue:
				"There are <b>{{count}}</b> items here without a value. Chat with your advisor to fill in the details.",
			zeroValue_one:
				"There is <b>{{count}}</b> item here without a value. Chat with your advisor to fill in the details.",
			zeroValue_few:
				"There are <b>{{count}}</b> items here without a value. Chat with your advisor to fill in the details.",
			zeroValue_other:
				"There are <b>{{count}}</b> items here without a value. Chat with your advisor to fill in the details.",
		},
		OverviewType: {
			Total: "Total assets",
			Cash: "Cash",
			Investment: "Investment",
			InvestmentProperty: "Real estate",
			Liabilities: "Liabilities",
			Other: "Other",
		},
		ImageCard: {
			title: "Might be useful",
			Mortgage: {
				title: "Simple mortgage calculator on a few touches",
				description: "Compare offers online or use our advisor",
			},
			Travel: {
				title: "Select a suitable trip",
				description: "Select a suitable trip and fill out the form",
			},
		},
		FinancialBalance: {
			title: "Financial balance",
			subtitlePositive: "Monthly you have on average",
			subtitleNegative: "Monthly you are missing on average",
			descriptionPositive:
				"Would you like to make the most of your budget surplus? Contact your advisor and start building a secure future today.",
			descriptionNegative:
				"Get in touch with your advisor and work together to find a way to improve your balance sheet.",
			startButton: "Start",
			clientNotFound:
				"Client was not found under the selected advisor, therefore you cannot view the financial balance. Please contact your advisor.",
			Modal1: {
				title: "Financial balance",
				description:
					"If you need to discuss the best way to work with the result, click the “I'm interested” button and this information will be sent to your advisor.",
				button: "I'm interested",
			},
			Modal2: {
				title: "Thank you for your interest",
				description:
					"Your advisor will contact you and suggest the best possible solution.",
			},
			contactAdvisorButton: "Contact advisor",
		},
		ShowAllButton: {
			title: "Show all",
		},
		UserType: {
			[UserTypeEnum.Client]: "Client",
			[UserTypeEnum.Agent]: "Advisor",
			[UserTypeEnum.Assistant]: "Assistant",
		},
		EmptyList: {
			label: "No content",
			description:
				"Your portfolio overview will appear once your advisor prepares and shares it.",
		},
	},
	Task: {
		Detail: {
			title: "Task",
			BasicInfo: {
				title: "Basic info",
				Name: "Name",
				Date: "Date",
				CreatedBy: "Created by",
				Solver: "Solver",
				Desc: "Description",
			},
			Process: {
				title: "Process",
			},
		},
	},
	Activity: {
		Detail: {
			title: "Activity",
			BasicInfo: {
				title: "Basic info",
				Name: "Name",
				Type: "Activity type",
				State: "State",
				Date: "Date",
				Address: "Address",
				CreatedBy: "Created by",
			},
			Participants: {
				title: "Participants",
				client: "Client",
			},
			Agents: {
				advisor: "Advisor",
				assistant: "Assistant",
			},
			OtherClient: "Other client",
			notes: "Entries and notes",
			ToolbarMenu: {
				WritteMessage: "Writte message",
				Call: "Call",
				WritteSMS: "Writte SMS",
				WritteEmail: "Writte E-mail",
			},
		},
		List: {
			title: "Activities",
			NoTask: "We are not currently working on any tasks.",
			NoContent: "No content",
			NoActivities:
				"Here you will see all past or scheduled activities with your advisor.",
			NoUpcomingEvents: "No upcoming events",
			NoOverdueEvents: "No overdue events",
			NoResolvedEvents: "No resolved events",
			EventType: {
				[ClientEventTypeEnum.Task]: "Task",
				[ClientEventTypeEnum.Activity]: "Activity",
			},
			ActivityType: {
				[ClientActivityTypeEnum.Call]: "Call",
				[ClientActivityTypeEnum.Meeting]: "Meeting",
				[ClientActivityTypeEnum.Other]: "Other",
				[ClientActivityTypeEnum.Administration]: "Administration",
			},
			Task: "Task",
			From: "from",
			To: "to",
		},
		Filters: {
			title: "Filter",
			Empty: "Empty",
			Term: {
				title: "Term",
				CustomPeriod: "Custom period",
				From: "From",
				To: "To",
				ChoosePeriod: "Choose period",
				WithoutPeriod: "Without period",
				Tomorrow: "Tomorrow",
				Today: "Today",
				Expired: "Expired",
			},
			Type: {
				title: "Activity type",
			},
			Created: {
				title: "Created",
				All: "All",
			},
		},
	},
	Portfolio: {
		Chart: {
			Value: "Value",
			Invested: "Invested",
			InvestedTogether: "Total invested",
			RemainsToPay: "Remains to pay",
			Paid: "Paid",
			Date: "Date",
			To: "To",
			Tooltip:
				"The overview is based on data from financial institutions, or provided by the advisor. For verification and more information, please contact your advisor.",
			ReportingDataUnavailable:
				"Not all data required to display the chart is currently available.",
		},
		Titles: {
			document: "Documents",
			portfolio: "Portfolio",
		},
		List: {
			BookmarkTileTitle: "Is the contract missing here?",
			BookmarkTileDesc:
				"Add the missing contract here so you have all of them in one place.",

			Tabs: {
				portfolio: "Portfolio",
				documents: "Documents",
			},
			Sorter: {
				title: "Sort by",
				Types: {
					default: "Areas",
					producer: "Producer",
					date: "Signing date",
				},
			},
			AddDocumentDrawer: {
				title: "Add document",
				Types: {
					photo: "Photo new document",
					gallery: "Upload document from gallery",
					file: "Upload document from files",
				},
			},
			FolderActionsDrawer: {
				title: "Folder actions",
				Actions: {
					rename: "Rename",
				},
			},
			DocumentActionsDrawer: {
				title: "Document actions",
				fileDrawerTitle: "Move to folder",
				folderDrawerTitle: "Folder actions",
				Actions: {
					download: "Download",
					removeDownload: "Remove from downloaded",
					rename: "Rename",
					delete: "Delete",
					share: "Share",
					move: "Move to folder",
				},
				Rename: {
					fileRename: "Document name",
					folderRename: "Folder name",
				},
			},
			MoveDocumentDialog: {
				title: "Move document",
				Actions: {
					create: "Create new folder",
				},
			},
			DeleteDocumentDialog: {
				title: "Delete document?",
				description:
					"Are you sure you want to delete the document {{documentName}}?",
				confirm: "Delete",
				cancel: "Cancel",
			},
			PortfolioDomainType: {
				[ClientPortfolioDomainEnum.BankingProducts]: "Banking products",
				[ClientPortfolioDomainEnum.BuildingSavings]: "Building savings",
				[ClientPortfolioDomainEnum.BusinessLoans]: "Business loans",
				[ClientPortfolioDomainEnum.Commodities]: "Commodities",
				[ClientPortfolioDomainEnum.Cryptocurrencies]: "Cryptocurrencies",
				[ClientPortfolioDomainEnum.Investments]: "Investments",
				[ClientPortfolioDomainEnum.Leasing]: "Leasing",
				[ClientPortfolioDomainEnum.LifeInsurance]: "Life insurance",
				[ClientPortfolioDomainEnum.Loans]: "Loans",
				[ClientPortfolioDomainEnum.PaidAdvisory]: "Paid advisory",
				[ClientPortfolioDomainEnum.PensionProducts]: "Pension products",
				[ClientPortfolioDomainEnum.PropertyInsurance]: "Property insurance",
				[ClientPortfolioDomainEnum.Services]: "Services",
				Uncategorized: "Uncategorized",
			},
			PortfolioSubType: {
				[ClientPropertyTypeEnum.AutoMoto]: "CAR/MOTO",
				[ClientPropertyTypeEnum.Flat]: "FLAT",
				[ClientPropertyTypeEnum.House]: "HOUSE",
				[ClientPropertyTypeEnum.Cottage]: "COTTAGE",
				[ClientPropertyTypeEnum.Land]: "LAND",
				[ClientPropertyTypeEnum.Art]: "ART",
				[ClientPropertyTypeEnum.Commodity]: "COMMODITY",
				[ClientPortfolioDomainEnum.LifeInsurance]: "LIFE INSURANCE",
				[ClientPortfolioDomainEnum.PropertyInsurance]: "NON-LIFE INSURANCE",
				[ClientPortfolioDomainEnum.PensionProducts]: "PENSION PRODUCT",
				[ClientPortfolioDomainEnum.BuildingSavings]: "BUILDING SAVINGS",
				[ClientPortfolioDomainEnum.Investments]: "INVESTMENT",
				[ClientPortfolioDomainEnum.BusinessLoans]: "LOAN FOR BUSINESS + SVJ",
				[ClientPortfolioDomainEnum.Loans]: "LOAN",
				[ClientPortfolioDomainEnum.Leasing]: "LEASING",
				[ClientPortfolioDomainEnum.Commodities]: "INVESTMENT COMMODITY",
				[ClientPortfolioDomainEnum.BankingProducts]: "BANK PRODUCT",
				[ClientPortfolioDomainEnum.Services]: "SERVICE",
				[ClientPortfolioDomainEnum.PaidAdvisory]: "PAID ADVISORY",
				[ClientPortfolioDomainEnum.Cryptocurrencies]: "CRYPTOCURRENCY",
			},
			Property: "Property",
			Skeleton: {
				label: "No content",
				description:
					"This page has no data, any available portfolio will be displayed here.",
			},
		},
	},
	PortfolioDetail: {
		SharedLabels: {
			name: "Name",
			contractNumber: "Contract number",
			producent: "Producent",
			product: "Product",
			signDate: "Signing date",
			beginDate: "Date of beginning",
			endDate: "Date of ending",
			untilAge: "until {{count}} years",
			Frequency: {
				[ClientPortfolioPaymentFrequencyEnum.Monthly]: "Monthly",
				[ClientPortfolioPaymentFrequencyEnum.Single]: "Single",
				[ClientPortfolioPaymentFrequencyEnum.Yearly]: "Yearly",
				[ClientPortfolioPaymentFrequencyEnum.Semiannually]: "Semi-annually",
				[ClientPortfolioPaymentFrequencyEnum.Quarterly]: "Quarterly",
				[ClientPortfolioPaymentFrequencyEnum.None]: "None",
			},
			investedAll: "Invested amount",
			targetAmount: "Target amount",
			paidAll: "Whole payment",
			empty: "No content",
			emptySubtitle:
				"This page has no data, please fill out the portfolio form in myDOCK.",
			Currency: {
				[CurrencyEnum.CZK]: "Kč",
				[CurrencyEnum.EUR]: "€",
				[CurrencyEnum.USD]: "$",
				[CurrencyEnum.GBP]: "£",
				[CurrencyEnum.JPY]: "¥",
				[CurrencyEnum.AUD]: "A$",
				[CurrencyEnum.SGD]: "S$",
				[CurrencyEnum.CHF]: "CHF",
				[CurrencyEnum.HUF]: "Ft",
				[CurrencyEnum.PLN]: "zł",
				[CurrencyEnum.NOK]: "kr",
				[CurrencyEnum.RON]: "lei",
				[CurrencyEnum.HKD]: "HK$",
				[CurrencyEnum.DKK]: "kr",
				[CurrencyEnum.CAD]: "C$",
				[CurrencyEnum.CNY]: "¥",
				[CurrencyEnum.NZD]: "NZ$",
				[CurrencyEnum.SEK]: "kr",
				[CurrencyEnum.TRY]: "₺",
				[CurrencyEnum.UNKNOWN]: "",
				Empty: "No currency",
			},
			CurrencyReverse: {
				[CurrencyEnum.CZK]: "CZK",
				[CurrencyEnum.EUR]: "EUR",
				[CurrencyEnum.USD]: "USD",
				[CurrencyEnum.GBP]: "GBP",
				[CurrencyEnum.JPY]: "JPY",
				[CurrencyEnum.AUD]: "AUD",
				[CurrencyEnum.SGD]: "SGD",
				[CurrencyEnum.CHF]: "CHF",
				[CurrencyEnum.HUF]: "HUF",
				[CurrencyEnum.PLN]: "PLN",
				[CurrencyEnum.NOK]: "NOK",
				[CurrencyEnum.RON]: "RON",
				[CurrencyEnum.HKD]: "HKD",
				[CurrencyEnum.DKK]: "DKK",
				[CurrencyEnum.CAD]: "CAD",
				[CurrencyEnum.CNY]: "CNY",
				[CurrencyEnum.NZD]: "NZD",
				[CurrencyEnum.SEK]: "SEK",
				[CurrencyEnum.TRY]: "TRY",
				[CurrencyEnum.UNKNOWN]: "",
				Empty: "No currency",
			},
		},
		LifeInsurance: {
			insuredAmount: "Insured",
			employersBonus: "Employers bonus",
			signDate: "Signing date",
			beginDate: "Date of beginning",
			endDate: "Date of ending",
			EnsuredPeople: {
				title: "Ensured people",
				Variant: {
					Constant: "Constant",
					Decreasing: "Decreasing",
					Annuities: "Annuities",
					Retroactively: "Retroactively",
					NonRetroactively: "Non-retroactively",
					Progression: "Progression",
					Empty: "Empty",
				},
			},
			Risk: {
				[ClientPortfolioInsuredPersonRiskEnum.Death]: "Death",
				[ClientPortfolioInsuredPersonRiskEnum.DeathByAccident]:
					"Death by accident",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity1]: "Invalidity 1",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity2]: "Invalidity 2",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity3]: "Invalidity 3",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity4]: "Invalidity 4",
				[ClientPortfolioInsuredPersonRiskEnum.SeriousIllness]:
					"Serious illness",
				[ClientPortfolioInsuredPersonRiskEnum.CancerFighting]:
					"For cancer fighting",
				[ClientPortfolioInsuredPersonRiskEnum.ForWoman]: "For woman",
				[ClientPortfolioInsuredPersonRiskEnum.DailyCompensationForInjury]:
					"Daily compensation for injury",
				[ClientPortfolioInsuredPersonRiskEnum.BodilyDamageInjury]:
					"Body damage injury",
				[ClientPortfolioInsuredPersonRiskEnum.IncapacityForWork]:
					"Incapacity for work",
				[ClientPortfolioInsuredPersonRiskEnum.Hospitalization]:
					"Hospitalization",
				[ClientPortfolioInsuredPersonRiskEnum.PermanentConsequencesOfInjuryFrom05Percent]:
					"Permanent consequences of injury from 0.5%",
				[ClientPortfolioInsuredPersonRiskEnum.PermanentConsequencesOfInjuryFrom10Percent]:
					"Permanent consequences of injury from 10%",
				[ClientPortfolioInsuredPersonRiskEnum.LoanPackage]: "Loan package",
			},
			InvestmentTool: {
				title: "Investment tool",
				IdentificationType: {
					[ClientPortfolioToolIdentificationTypeEnum.Isin]: "ISIN",
					[ClientPortfolioToolIdentificationTypeEnum.Other]: "Identification",
					[ClientPortfolioToolIdentificationTypeEnum.Crypto]: "Crypto",
					[ClientPortfolioToolIdentificationTypeEnum.BankProduct]:
						"Bank Product",
					[ClientPortfolioToolIdentificationTypeEnum.Null]: "Null",
					[ClientPortfolioToolIdentificationTypeEnum.None]: "None",
				},
				reocuring: "Regular payment",
				invested: "Total invested",
				value: "Current value",
				type: "Tool type",
				Type: {
					[ClientPortfolioToolTypeEnum.Fund]: "Funds",
					[ClientPortfolioToolTypeEnum.Bond]: "Bonds",
					[ClientPortfolioToolTypeEnum.Other]: "Other",
					[ClientPortfolioToolTypeEnum.Commodity]: "Commodities",
					[ClientPortfolioToolTypeEnum.Crypto]: "Cryptocurrencies",
					[ClientPortfolioToolTypeEnum.BankProduct]: "Bank Products",
					[ClientPortfolioToolTypeEnum.Share]: "Shares",
				},
			},
		},
		NonLifeInsurance: {
			name: "Name",
			contractNumber: "Contract number",
			producent: "Producent",
			product: "Product",
			insured: "Insured",
			insurancePeriod: "Term insurance",
			AdditionalInfo: {
				signDate: "Signing date",
				beginDate: "Date of beginning",
				endDate: "Date of ending",
			},
		},
		PensionProduct: {
			participantContribution: "Participant contribution",
			employerContribution: "Employer contribution",
			toAge: "To client's age",
		},
		BuildingSavings: {
			savings: "Savings",
		},
		LoanForBusiness: {
			endOfFixation: "End of fixation",
		},
		Loan: {
			coDebtors: "Co-debtors",
			amount: "Amount",
			matury: "Maturity",
			interestRate: "Interest rate",
			installment: "Installment",
			Frequency: {
				Years: "{{count}} year",
				Years_one: "{{count}} year",
				Years_few: "{{count}} years",
				Years_other: "{{count}} years",
				Months: "{{count}} month",
				Months_one: "{{count}} month",
				Months_few: "{{count}} months",
				Months_other: "{{count}} months",
			},
		},
		Services: {
			name: "Name",
			contractNumber: "Contract number",
			producent: "Producent",
			product: "Product",
			insured: "Insured",
			cardLabel: "Contract parameters",
			advancePayment: "Advance payment",
			tariff: "Tariff",
			commodity: "Commodity",
			pricePerUnit: "Price for {{unit}}",
			fixedPrice: "Fixed price",
			CommodityService: {
				[ClientPortfolioDataCommodityServiceEnum.Electricity]: "Electricity",
				[ClientPortfolioDataCommodityServiceEnum.Gas]: "Gas",
				[ClientPortfolioDataCommodityServiceEnum.Water]: "Water",
				[ClientPortfolioDataCommodityServiceEnum.Internet]: "Internet",
				[ClientPortfolioDataCommodityServiceEnum.Television]: "Television",
				[ClientPortfolioDataCommodityServiceEnum.CellPhoneTarrif]:
					"Cell phone tariff",
				[ClientPortfolioDataCommodityServiceEnum.Other]: "Other",
				[ClientPortfolioDataCommodityServiceEnum.None]: "None",
			},
			AdditionalInfo: {
				signDate: "Signing date",
				beginDate: "Date of beginning",
				endDate: "Date of ending",
				depositAmount: "Amount of the deposit",
			},
		},
		PaidAdvisory: {
			fixedPrice: "Fixed price",
		},
		ToolHistory: {
			title: "Instruction history",
			active: "Active",
			nonActive: "Non-active",
			button: "Actions history",
			purchase: "Purchase",
			ActionType: {
				[ClientPortfolioInvestmentTypeEnum.Buy]: "Buying",
				[ClientPortfolioInvestmentTypeEnum.Sell]: "Selling",
				[ClientPortfolioInvestmentTypeEnum.Empty]: "",
			},
			ActionTypeBankProduct: {
				[ClientPortfolioInvestmentTypeEnum.Buy]: "Deposit",
				[ClientPortfolioInvestmentTypeEnum.Sell]: "Withdrawal",
				[ClientPortfolioInvestmentTypeEnum.Empty]: "",
			},
			Fund: {
				type: "Instruction type",
				startDate: "Start date of instruction",
				endDate: "End date of instruction",
				amount: "Amount",
				spread: "Spread",
				transactionType: "Transaction type",
				disposition: "More disposition",
				entryTransaction: "Entry transaction",
				entryTransactionAmount: "Transaction amount",
				entryTransactionProcantage: "Charged on first payments",
				noFee: "No fee",
				applying: "Applying",
				Applying: {
					[ClientPortfolioInvestmentEnterFeeApplyingEnum.ChargedFeeFromInvestment]:
						"Charged from investment",
					[ClientPortfolioInvestmentEnterFeeApplyingEnum.PaidExtra]:
						"Paid extra",
				},
				Frequency: {
					[ClientPortfolioInvestmentFrequencyEnum.Monthly]: "Monthly",
					[ClientPortfolioInvestmentFrequencyEnum.EveryTwoMonths]:
						"Every two months",
					[ClientPortfolioInvestmentFrequencyEnum.Quarter]: "Quarterly",
					[ClientPortfolioInvestmentFrequencyEnum.SemiYearly]: "Semi-annually",
					[ClientPortfolioInvestmentFrequencyEnum.Yearly]: "Yearly",
					[ClientPortfolioInvestmentFrequencyEnum.Once]: "Once",
					[ClientPortfolioInvestmentFrequencyEnum.None]: "None",
				},
				TransactionType: {
					[ClientPortfolioInvestmentTransactionEnum.Trade]: "Trade",
					[ClientPortfolioInvestmentTransactionEnum.FundTransfer]:
						"Fund transfer",
				},
				EntryTransaction: {
					[ClientPortfolioInvestmentEnterFeeTypeEnum.Once]: "Once",
					[ClientPortfolioInvestmentEnterFeeTypeEnum.FromFirstPayments]:
						"From first payments",
					[ClientPortfolioInvestmentEnterFeeTypeEnum.FromEveryPayment]:
						"From every payment",
				},
				EntryTransactionAmount: {
					[ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Amount]: "Kč",
					[ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Percent]: "%",
				},
			},
			Bond: {
				issueDate: "Issue date",
				maturityDate: "Maturity date",
				value: "Value",
				amount: "Number of units",
				interestRate: "Interest rate/coupon",
				frequency: "Payment frequency",
				yield: "Yield p.a.",
				tax: "Taxation",
				fee: "Fee according to frequency",
				InterestType: {
					[ClientBondInterestTypeEnum.Fixed]: "Fixed interest",
					[ClientBondInterestTypeEnum.Variable]: "Variable interest",
					[ClientBondInterestTypeEnum.Nullable]: "Null coupon",
				},
			},
			Cryptocurrency: {
				title: "Cryptocurrency detail",
				name: "Cryptocurrency",
				holdingType: "Holding type",
				amount: "Amount",
				reocuring: "Regular payment",
				invested: "Total invested",
				value: "Current value",
				HoldingType: {
					[ClientPortfolioInstructionHoldingTypeEnum.InAdministration]:
						"In administration",
					[ClientPortfolioInstructionHoldingTypeEnum.OwnWallet]:
						"In own wallet",
				},
			},
			Commodity: {
				title: "Commodities detail",
				regularPayment: "Regular payment",
				invested: "Total invested",
				value: "Current value",
			},
			BankProducts: {
				title: "Account detail",
				name: "Account name",
				currentValue: "Current value",
				evaluation: "Evaluation",
			},
		},
		Investment: {
			InvestmentTool: {
				title: "Detail of investment",
				reocurringPayment: "Reocurring payment",
				invested: "Total invested",
				value: "Current value",
			},
		},
		Property: {
			TypeOfConstruction: "Type of construction",
			PropertyCategoryValue: {
				[ClientPropertyCategoryEnum.Apartment]: "Apartment",
				[ClientPropertyCategoryEnum.OnKey]: "OnKey",
				[ClientPropertyCategoryEnum.LowEnergy]: "LowEnergy",
				[ClientPropertyCategoryEnum.Family]: "Family",
				[ClientPropertyCategoryEnum.Villa]: "Villa",
				[ClientPropertyCategoryEnum.Garage]: "Garage",
				[ClientPropertyCategoryEnum.HistoricalObject]: "HistoricalObject",
				[ClientPropertyCategoryEnum.Cottage]: "Cottage",
				[ClientPropertyCategoryEnum.CivicAmenityBuilding]:
					"CivicAmenityBuilding",
				[ClientPropertyCategoryEnum.Pond]: "Pond",
				[ClientPropertyCategoryEnum.Farmstead]: "Farmstead",
				[ClientPropertyCategoryEnum.OtherBuliding]: "OtherBuliding",
				[ClientPropertyCategoryEnum.Livving]: "Livving",
				[ClientPropertyCategoryEnum.CommercialBuilding]: "CommercialBuilding",
				[ClientPropertyCategoryEnum.Garden]: "Garden",
				[ClientPropertyCategoryEnum.PermanentGrassGrowth]:
					"PermanentGrassGrowth",
				[ClientPropertyCategoryEnum.Forest]: "Forest",
				[ClientPropertyCategoryEnum.AgriculturalLand]: "AgriculturalLand",
				[ClientPropertyCategoryEnum.OtherLand_]: "OtherLand_",
				[ClientPropertyCategoryEnum.None]: "None",
			},
			PropertyType: {
				[ClientPropertyTypeEnum.Art]: "Art",
				[ClientPropertyTypeEnum.AutoMoto]: "AutoMoto",
				[ClientPropertyTypeEnum.Commodity]: "Commodity",
				[ClientPropertyTypeEnum.Cottage]: "Other property",
				[ClientPropertyTypeEnum.Flat]: "Flat",
				[ClientPropertyTypeEnum.House]: "House",
				[ClientPropertyTypeEnum.Land]: "Land",
			},
			LandTypeEnum: {
				[ClientPropertyCategoryEnum.Apartment]: "Apartment",
				[ClientPropertyCategoryEnum.OnKey]: "OnKey",
				[ClientPropertyCategoryEnum.LowEnergy]: "LowEnergy",
				[ClientPropertyCategoryEnum.Family]: "Family",
				[ClientPropertyCategoryEnum.Villa]: "Villa",
				[ClientPropertyCategoryEnum.Garage]: "Garage",
				[ClientPropertyCategoryEnum.HistoricalObject]: "HistoricalObject",
				[ClientPropertyCategoryEnum.Cottage]: "Cottage",
				[ClientPropertyCategoryEnum.CivicAmenityBuilding]:
					"CivicAmenityBuilding",
				[ClientPropertyCategoryEnum.Pond]: "Pond",
				[ClientPropertyCategoryEnum.Farmstead]: "Farmstead",
				[ClientPropertyCategoryEnum.OtherBuliding]: "OtherBuliding",
				[ClientPropertyCategoryEnum.Livving]: "Livving",
				[ClientPropertyCategoryEnum.CommercialBuilding]: "CommercialBuilding",
				[ClientPropertyCategoryEnum.Garden]: "Garden",
				[ClientPropertyCategoryEnum.PermanentGrassGrowth]:
					"PermanentGrassGrowth",
				[ClientPropertyCategoryEnum.Forest]: "Forest",
				[ClientPropertyCategoryEnum.AgriculturalLand]: "AgriculturalLand",
				[ClientPropertyCategoryEnum.OtherLand_]: "OtherLand_",
				[ClientPropertyCategoryEnum.None]: "None",
			},
			SecondaryBuildingArea: "Secondary building area",
			MarketPriceHistory: "Property market price history",
			LandType: "Land type",
			StreetAndNumber: "Street and number",
			Municipality: "Municipality",
			ZipCode: "ZIP Code",
			ReproductionPrice: "Reproduction price",
			MarketPrice: "Market price",
			InteriorPrice: "Interior price",
			LandPrice: "Land price",
			LandArea: "Land area",
			Ownership: "Ownership",
			OwnershipValue: {
				[ClientPropertyOwnershipEnum.Cooperative]: "Cooperative",
				[ClientPropertyOwnershipEnum.DEPRECATED_EMPTY]: "Empty",
				[ClientPropertyOwnershipEnum.National]: "National",
				[ClientPropertyOwnershipEnum.NationalMunicipal]: "National/Municipal",
				[ClientPropertyOwnershipEnum.None]: "None",
				[ClientPropertyOwnershipEnum.Personal]: "Personal",
				[ClientPropertyOwnershipEnum.Other]: "Other",
			},
			Disposition: "Disposition",
			DispositionValue: {
				[ClientPropertyBuildingDispositionEnum.None]: "None",
				[ClientPropertyBuildingDispositionEnum.OnePlusKK]: "1+kk",
				[ClientPropertyBuildingDispositionEnum.OnePlusOne]: "1+1",
				[ClientPropertyBuildingDispositionEnum.TwoPlusKK]: "2+kk",
				[ClientPropertyBuildingDispositionEnum.TwoPlusOne]: "2+1",
				[ClientPropertyBuildingDispositionEnum.ThreePlusKK]: "3+kk",
				[ClientPropertyBuildingDispositionEnum.ThreePlusOne]: "3+1",
				[ClientPropertyBuildingDispositionEnum.FourPlusKK]: "4+kk",
				[ClientPropertyBuildingDispositionEnum.FourPlusOne]: "4+1",
				[ClientPropertyBuildingDispositionEnum.FivePlusKK]: "5+kk",
				[ClientPropertyBuildingDispositionEnum.FivePlusOne]: "5+1",
				[ClientPropertyBuildingDispositionEnum.SixPlusKK]: "6+kk",
				[ClientPropertyBuildingDispositionEnum.SixPlusOne]: "6+1",
				[ClientPropertyBuildingDispositionEnum.SevenPlusKK]: "7+kk",
				[ClientPropertyBuildingDispositionEnum.SevenPlusOne]: "7+1",
				[ClientPropertyBuildingDispositionEnum.Atypical]: "Atypical",
				[ClientPropertyBuildingDispositionEnum.StudioApartment]:
					"Studio apartment",
				[ClientPropertyBuildingDispositionEnum.Custom]: "Custom",
			},
			Floors: "Floors",
			NumberOfFloors: "Number of Floors",
			NumberOfAboveGroundFloors: "Number of above-ground floors",
			BuildingMaterial: "Building material",
			BuildingMaterialValue: {
				[ClientPropertyBuildingMaterialEnum.Brick]: "Brick",
				[ClientPropertyBuildingMaterialEnum.Custom]: "Custom",
				[ClientPropertyBuildingMaterialEnum.Empty]: "Empty",
				[ClientPropertyBuildingMaterialEnum.Mixed]: "Mixed",
				[ClientPropertyBuildingMaterialEnum.None]: "None",
				[ClientPropertyBuildingMaterialEnum.Panel]: "Panel",
				[ClientPropertyBuildingMaterialEnum.ReinforcedConcrete]:
					"Reinforced concrete",
				[ClientPropertyBuildingMaterialEnum.Skeletal]: "Skeletal",
				[ClientPropertyBuildingMaterialEnum.Stone]: "Stone",
				[ClientPropertyBuildingMaterialEnum.Wood]: "Wood",
			},
			FloorArea: "Floor area",
			UsableArea: "Usable area",
			Renovation: "Renovation",
			RenovationValue: {
				[ClientPropertyRenovationEnum.Continuous]: "Continuous",
				[ClientPropertyRenovationEnum.InProgress]: "In progress",
				[ClientPropertyRenovationEnum.None]: "None",
				[ClientPropertyRenovationEnum.Null]: "Null",
				[ClientPropertyRenovationEnum.Total]: "Total",
				[ClientPropertyRenovationEnum.DEPRECATED_EMPTY]: "Empty",
			},
			YearOfRenovation: "Year of renovation",
			Properties: "Properties",
			ApartmentNumber: "Apartment number",
			HouseholdArea: "Household area",
			ItemTitle: "Item title",
			AdditionalDescription: "Additional description",
			Description: "Description",
			PurchaseDate: "Purchase date",
			Value: "Value",
			CarPropertyType: "Car type",
			CarPropertyTypeValue: {
				[ClientPropertyVehicleTypeEnum.Bus]: "Bus",
				[ClientPropertyVehicleTypeEnum.Camper]: "Camper",
				[ClientPropertyVehicleTypeEnum.CommercialVehicle]: "Commercial vehicle",
				[ClientPropertyVehicleTypeEnum.MotorbikeATV]: "Motorbike/ATV",
				[ClientPropertyVehicleTypeEnum.None]: "None",
				[ClientPropertyVehicleTypeEnum.Other]: "Other",
				[ClientPropertyVehicleTypeEnum.OtherElectro]: "Other electro",
				[ClientPropertyVehicleTypeEnum.Passenger]: "Passenger vehicle",
				[ClientPropertyVehicleTypeEnum.Tractor]: "Tractor",
				[ClientPropertyVehicleTypeEnum.Trailer]: "Trailer",
				[ClientPropertyVehicleTypeEnum.Truck]: "Truck",
				[ClientPropertyVehicleTypeEnum.WorkingMachine]: "Working machine",
			},
			CarBodyType: "Car body type",
			CarBodyTypeValue: {
				[ClientCarBodyTypeEnum.Cabriolet]: "Cabriolet",
				[ClientCarBodyTypeEnum.Combi]: "Combi",
				[ClientCarBodyTypeEnum.Coupe]: "Coupe",
				[ClientCarBodyTypeEnum.Hatchback]: "Hatchback",
				[ClientCarBodyTypeEnum.Limousine]: "Limousine",
				[ClientCarBodyTypeEnum.Mpv]: "MPV",
				[ClientCarBodyTypeEnum.None]: "None",
				[ClientCarBodyTypeEnum.OffRoad]: "Off-Road",
				[ClientCarBodyTypeEnum.Other]: "Other",
				[ClientCarBodyTypeEnum.PickUp]: "Pick-Up",
				[ClientCarBodyTypeEnum.Roadster]: "Roadster",
				[ClientCarBodyTypeEnum.Sedan]: "Sedan",
				[ClientCarBodyTypeEnum.Suv]: "SUV",
				[ClientCarBodyTypeEnum.Van]: "Van",
				[ClientCarBodyTypeEnum.liftback]: "Liftback",
				[ClientCarBodyTypeEnum.DEPRECATED_EMPTY]: "Empty",
			},
			CarVehicleType: "Car vehicle type",
			CarVehicleTypeValue: {
				[ClientPropertyCarVehicleTypeEnum.ElectricScooter]: "Electric scooter",
				[ClientPropertyCarVehicleTypeEnum.Empty]: "Empty",
				[ClientPropertyCarVehicleTypeEnum.GardenTractor]: "Garden tractor",
				[ClientPropertyCarVehicleTypeEnum.GolfCart]: "Golf cart",
				[ClientPropertyCarVehicleTypeEnum.Kart]: "Kart",
				[ClientPropertyCarVehicleTypeEnum.None]: "None",
				[ClientPropertyCarVehicleTypeEnum.Other]: "Other",
				[ClientPropertyCarVehicleTypeEnum.Segway]: "Segway",
			},
			CarLicensePlate: "Car license plate",
			CarBrand: "Car brand",
			CarVin: "VIN code",
			CarPurchaseDate: "Car purchase date",
			CarRegistration: "Car registration",
			CreatedAt: "Created at",
			CarPutIntoService: "Car put into service",
			CarPower: "Car power",
			CarVolume: "Car volume",
			CarWeight: "Car weight",
			CarSeatCount: "Car seat count",
			CarFuelType: "Car fuel type",
			CarFuelTypeValue: {
				[ClientCarFuelEnum.Cng]: "CNG",
				[ClientCarFuelEnum.Diesel]: "Diesel",
				[ClientCarFuelEnum.DieselLpg]: "Diesel + LPG",
				[ClientCarFuelEnum.Electricity]: "Electricity",
				[ClientCarFuelEnum.HybridDiesel]: "Hybrid Diesel",
				[ClientCarFuelEnum.HybridPetrol]: "Hybrid Petrol",
				[ClientCarFuelEnum.Lpg]: "LPG",
				[ClientCarFuelEnum.None]: "None",
				[ClientCarFuelEnum.Other]: "Other",
				[ClientCarFuelEnum.Petrol]: "Petrol",
				[ClientCarFuelEnum.PetrolCng]: "Petrol + CNG",
				[ClientCarFuelEnum.PetrolLpg]: "Petrol + LPG",
			},
			CarSecurity: "Car security",
			CarAlarmSecurity: "Car alarm security",
			CarActiveTrackingSecurity: "Car active tracking security",
			CarMechanicalSecurity: "Car mechanical security",
			CarImmobilizerSecurity: "Car immobilizer security",
			CarGlassEtchingSecurity: "Car glass etching security",
			CarPassiveTrackingSecurity: "Car passive tracking security",
			CarSpecialEquipment: "Car special equipment",
			CarSpecialEquipment44: "Car special equipment 44",
			CarSpecialEquipmentAudiovisualSystem: "Car audiovisual system",
			CarSpecialEquipmentLeatherUpholstery: "Car leather upholstery",
			CarSpecialEquipmentPanoramicRoof: "Car panoramic roof",
			CarSpecialEquipmentParkingAssistant: "Car parking assistant",
			CarValue: "Car value",
			VehicleParameters: "Vehicle parameters",
			Category: "Category",
			CategoryValue: {
				[ClientPropertyCategoryEnum.Apartment]: "Apartment",
				[ClientPropertyCategoryEnum.OnKey]: "On key",
				[ClientPropertyCategoryEnum.LowEnergy]: "Low energy",
				[ClientPropertyCategoryEnum.Family]: "Family",
				[ClientPropertyCategoryEnum.Villa]: "Villa",
				[ClientPropertyCategoryEnum.Garage]: "Garage",
				[ClientPropertyCategoryEnum.HistoricalObject]: "Historical object",
				[ClientPropertyCategoryEnum.Cottage]: "Cottage",
				[ClientPropertyCategoryEnum.CivicAmenityBuilding]:
					"Civic amenity building",
				[ClientPropertyCategoryEnum.Pond]: "Pond",
				[ClientPropertyCategoryEnum.Farmstead]: "Farmstead",
				[ClientPropertyCategoryEnum.OtherBuliding]: "Other building",
				[ClientPropertyCategoryEnum.Livving]: "Living",
				[ClientPropertyCategoryEnum.CommercialBuilding]: "Commercial building",
				[ClientPropertyCategoryEnum.Garden]: "Garden",
				[ClientPropertyCategoryEnum.PermanentGrassGrowth]:
					"Permanent grass growth",
				[ClientPropertyCategoryEnum.Forest]: "Forest",
				[ClientPropertyCategoryEnum.AgriculturalLand]: "Agricultural land",
				[ClientPropertyCategoryEnum.OtherLand_]: "Other land",
				[ClientPropertyCategoryEnum.None]: "None",
			},
			Terrace: "Terrace",
			Loggia: "Loggia",
			Balcony: "Balcony",
			Garage: "Garage",
			Cellar: "Cellar",
			Elevator: "Elevator",
			BarrierFree: "Barrier-free",
			SwimmingPool: "Swimming pool",
			ParkingSpace: "Parking space",
			Furnished: "Furnished",
			YearOfCompletion: "Year of completion",
		},
	},
	Finances: {
		title: "Financial balance",
		Intro: {
			title: "Fill out your financial balance",
			description: "You don't have any income or expenses yet",
			button: "Fill out",
			result: "Result",
			surplusTitle: "Use the surplus",
			surplusDescription:
				"On average monthly, you have <balance>{{balance}}</balance>. This surplus can be further evaluated. Contact your advisor and manage your finances effectively.",
			deficitTitle: "Opportunities to balance the budget",
			deficitDescription:
				"Your expenses exceed your income by <balance>{{balance}}</balance> per month. Contact your advisor and find a way to improve the balance together.",
			contactAdvisor: "Contact advisor",
		},
		Income: {
			titleTentatively: "Monthly income",
			descriptionTentative:
				"To tell you what your financial balance is, we need to know your income.",
			titleExact: "Enter in detail",
			descriptionExact: {
				intro:
					"If you have several different types of income and want to have them all clearly listed, you can use the detailed entry option.",
				categories: [
					{
						title: "Main regular income, e.g.:",
						items: ["salary", "wage", "business income"],
					},
					{
						title: "Additional income, e.g.:",
						items: [
							"part-time jobs",
							"honorariums",
							"contract income",
							"other side activities",
						],
					},
					{
						title: "Passive income not requiring active work, e.g.:",
						items: [
							"rent",
							"dividends",
							"investment returns",
							"licensing fees",
						],
					},
					{
						title: "Income from the state, e.g.:",
						items: [
							"parental allowance",
							"pension",
							"unemployment benefits",
							"other social benefits",
						],
					},
					{
						title:
							"Other income that doesn't fit into the above categories, e.g.:",
						items: ["winnings", "gifts", "income from abroad"],
					},
				],
			},
			subtitleExact: "What to count?",
			readMore: "Read more",
			readLess: "Hide",
			addIncome: "Add income",
			continue: "Continue to expenses",
		},
		IncomeModal: {
			modalTitle: "Income",
			name: "Custom name",
			amount: "Amount",
		},
		Expenses: {
			titleTentatively: "Monthly expenses",
			descriptionTentative:
				"We also need to know your expenses so that we can calculate how much money you have left each month.",
			titleExact: "Enter in detail",
			subtitleExact: "What to count?",
			descriptionExact:
				"If you have different types of expenses and want to have them all neatly filled in, you can use their detailed entry.",
			whatToCountDescription:
				"Main regular expenses – e.g., household costs, housing, transportation, or insurance. Savings – amounts that you regularly set aside (savings, investments, supplementary pension insurance). Tax expenses – e.g., income tax advances or other mandatory payments. Other expenses – all other regular or significant expenses that do not fall into the above categories.",
			minimumValueDescription: "min. amount",
			Types: {
				[ClientFinancialPlanExpenseTypeEnum.Household]: {
					title: "Household",
					description:
						"Enter regular expenses associated with the normal running of your household – e.g., groceries, drugstore items, services (internet), and other necessities.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Housing]: {
					title: "Housing",
					description:
						"Enter all housing-related expenses – e.g., mortgage, rent, utilities, housing-related services, energy, repair fund, maintenance.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Insurance]: {
					title: "Insurance",
					description:
						"Enter all your payments for life, property, and other insurance, or for health and social insurance (self-employed persons).",
				},
				[ClientFinancialPlanExpenseTypeEnum.Saving]: {
					title: "Savings",
					description:
						"Enter the amounts you regularly set aside for savings accounts, investments, supplementary pension insurance, or other forms of savings.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Taxes]: {
					title: "Taxes",
					description:
						"Enter expenses related to tax payments – e.g., income tax advances, tax advisor fees, etc.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Traffic]: {
					title: "Transport",
					description:
						"Enter your transportation costs – including car payments, fuel, public transportation, vehicle insurance, and maintenance.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Other]: {
					title: "Other",
					description:
						"Enter any other regular or significant expenses that do not fall into the above categories.",
				},
			},
			Names: {
				[ClientFinancialPlanExpenseNameEnum.Rent]: "Rent",
				[ClientFinancialPlanExpenseNameEnum.EnergyBills]: "Energy",
				[ClientFinancialPlanExpenseNameEnum.Fees]: "Fees",
				[ClientFinancialPlanExpenseNameEnum.OtherHousing]: "Other housing",
				[ClientFinancialPlanExpenseNameEnum.Leasing]: "Leasing",
				[ClientFinancialPlanExpenseNameEnum.TollFees]: "Toll fees",
				[ClientFinancialPlanExpenseNameEnum.CarInsurance]: "Car insurance",
				[ClientFinancialPlanExpenseNameEnum.FuelExpenses]: "Fuel expenses",
				[ClientFinancialPlanExpenseNameEnum.Maintenance]: "Maintenance",
				[ClientFinancialPlanExpenseNameEnum.Fares]: "Fares",
				[ClientFinancialPlanExpenseNameEnum.BuildingSavings]:
					"Building savings",
				[ClientFinancialPlanExpenseNameEnum.PensionProducts]:
					"Pension products",
				[ClientFinancialPlanExpenseNameEnum.Investments]: "Investments",
				[ClientFinancialPlanExpenseNameEnum.LifeInsurance]: "Life insurance",
				[ClientFinancialPlanExpenseNameEnum.HouseholdInsurance]:
					"Household insurance",
				[ClientFinancialPlanExpenseNameEnum.Responsibility]: "Responsibility",
				[ClientFinancialPlanExpenseNameEnum.FoodExpenses]: "Food expenses",
				[ClientFinancialPlanExpenseNameEnum.PocketMoney]: "Pocket money",
				[ClientFinancialPlanExpenseNameEnum.Hairdressing]: "Hairdressing",
				[ClientFinancialPlanExpenseNameEnum.Clothing]: "Clothing",
				[ClientFinancialPlanExpenseNameEnum.Sport]: "Sport",
				[ClientFinancialPlanExpenseNameEnum.Culture]: "Culture",
				[ClientFinancialPlanExpenseNameEnum.Restaurants]: "Restaurants",
				[ClientFinancialPlanExpenseNameEnum.Pets]: "Pets",
				[ClientFinancialPlanExpenseNameEnum.Subscription]: "Subscription",
				[ClientFinancialPlanExpenseNameEnum.Books]: "Books",
				[ClientFinancialPlanExpenseNameEnum.MobilePhone]: "Mobile phone",
				[ClientFinancialPlanExpenseNameEnum.TuitionFees]: "Tuition fees",
				[ClientFinancialPlanExpenseNameEnum.Gifts]: "Gifts",
				[ClientFinancialPlanExpenseNameEnum.Vacation]: "Vacation",
				[ClientFinancialPlanExpenseNameEnum.CharityContributions]:
					"Charity contributions",
				[ClientFinancialPlanExpenseNameEnum.RegularCleaning]:
					"Regular cleaning",
				[ClientFinancialPlanExpenseNameEnum.AlimonyPayments]:
					"Alimony payments",
				[ClientFinancialPlanExpenseNameEnum.Babysitter]: "Babysitter",
				[ClientFinancialPlanExpenseNameEnum.Other]: "Other",
				[ClientFinancialPlanExpenseNameEnum.Loans]: "Loans",
				[ClientFinancialPlanExpenseNameEnum.Cigarettes]: "Cigarettes",
				[ClientFinancialPlanExpenseNameEnum.SocialTax]: "Social tax",
				[ClientFinancialPlanExpenseNameEnum.IncomeTax]: "Income tax",
				[ClientFinancialPlanExpenseNameEnum.PropertyTax]: "Property tax",
				[ClientFinancialPlanExpenseNameEnum.Total]: "Total",
			},
		},
		ExpensePicker: {
			title: "Expenses",
			value: "Monthly payment",
			minimumValue: "Minimum value",
			description:
				"Enter the minimum amount you will always have to spend on this expense, even if your income decreases.",
		},
		Overview: {
			title: "Result",
			description: "The result of your financial balance is:",
			balancePossitive: "Use balance",
			balancePossitiveDescription:
				"Your current surplus of <balance>{{balance}}</balance> Kč per month opens up opportunities to work more efficiently with your finances – for example, through savings, investments, or optimizing your financial plan.",
			balanceNegative: "Opportunities to balance the budget",
			balanceNegativeDescription:
				"Your expenses exceed your income by <balance>{{balance}}</balance> per month. We recommend consulting with your advisor – they can help, for example, with optimizing expenses, creating a savings plan, or finding other solutions.",
			contactAdvisor: "Contact advisor",
			finishButton: "Finish",
			checkButton: "Check my income and expenses",
		},
		Offline: {
			editError: "Financial balance cannot be edited in offline mode.",
		},
	},
	Notifications: {
		title: "Notifications",
		readAll: "Mark all as read",
		term: "Term",
		Tabs: {
			unread: "Unread",
			all: "All",
		},
		Date: {
			today: "Today",
			daysAgo: "{{count}} days ago",
			daysAgo_one: "Yesterday",
			daysAgo_other: "{{count}} days ago",
			lastWeek: "Last week",
		},
		noContent: "There are currently no unread notifications",
	},
	DataCard: {
		BasicInfo: {
			title: "Basic informations",
			additionalInfo: "Additional informations",
		},
		Documents: {
			title: "Documents",
		},
		RelatedContracts: {
			title: "Related contracts",
		},
		RelatedProperties: {
			title: "Related properties",
		},
		RelatedServices: {
			title: "Related services",
		},
		PropertyValue: {
			title: "Property value",
		},
		PropertyParameters: {
			title: "Property parameters",
		},
		Value: {
			title: "Value",
		},
	},
	ExpenseCard: {
		addExpense: "Add expense",
	},
	Form: {
		Validations: {
			required: "This field is required",
		},
	},
	DocumentDetail: {
		noDocuments: "No documents found",
	},
	BarChart: {
		income: "Income",
		expenses: "Expenses",
	},
	Goals: {
		List: {
			title: "My Goals",
			activeGoals: "Active Goals",
			completed: "Completed",
			ctaTitle: "Have another dream?",
			ctaTitleCompleted: "What is your next plan?",
			ctaDescription: "Tell us what you want and see the path to get there.",
			ctaButton: "I want a new goal",
			Status: {
				onTrack: "On track",
				warning: "Needs attention",
				offTrack: "Off track",
				unplanned: "Not planned",
			},
			DueIn: {
				Years: "in {{count}} year",
				Years_one: "in {{count}} year",
				Years_few: "in {{count}} years",
				Years_other: "in {{count}} years",
				Months: "in {{count}} month",
				Months_one: "in {{count}} month",
				Months_few: "in {{count}} months",
				Months_other: "in {{count}} months",
			},
		},
		EmptyState: {
			title: "Where are you heading?",
			description:
				"Tell us what you want — and your agent will help find the way to get there.",
			addGoalButton: "I want to set a goal",
			getAdviceButton: "Get advice",
			examplesTitle: "Where others started",
			addLabel: "Add",
			Examples: {
				Retirement: {
					name: "Financial freedom",
					reason: "When you no longer have to go to work.",
				},
				Housing: {
					name: "Keys to your own place",
					reason: "No more paying rent to strangers.",
				},
				Vacation: {
					name: "Dream vacation",
					reason: "The trip you keep putting off.",
				},
			},
		},
		Detail: {
			title: "My goal",
			dueInYear: "In {{dueIn}} ({{year}})",
			completedInYear: "Completed {{year}}",
			Blocked: {
				title: "Action restricted",
				heading: "Your advisor manages this action",
				description:
					"This goal has contracts or settings pinned by your advisor. If you want a change, arrange it via chat.",
				cta: "Message advisor",
			},
			WelcomePin: {
				title: "Goal created!",
				WithOwnFunds: {
					description:
						"You said you have {{amount}} CZK set aside. Where do you keep this money?",
					cta: "Assign the money set aside",
				},
				WithoutOwnFunds: {
					description:
						"Do you have a contract in your portfolio that this goal could be filled with?",
					cta: "Yes, pick a contract",
				},
				dismiss: "Not yet",
			},
			Status: {
				onTrack: "On track",
				warning: "Needs attention",
				offTrack: "Off track",
				completed: "Completed",
				unplanned: "Not planned",
			},
			OwnFunds: {
				title: "Set aside ({{amount}} CZK)",
				emptyTitle: "Tell us where it sits",
				emptyDescription:
					"Pin a savings account, fund, or wherever you keep this money.",
				pinContract: "Pin a contract",
			},
			Solution: {
				title: "Remaining to save ({{amount}} CZK)",
				emptyTitle: "This goal has no contracts yet",
				emptyDescription:
					"Pin an existing contract from your portfolio or add a future one.",
				valueLabel: "{{amount}} CZK",
				allocationLabel: "{{percent}} % of contract",
				monthlyLabel: "{{amount}} CZK/month",
				expectedInYear: "→ {{amount}} in {{year}}",
				futureBadge: "Future",
				loanLabel: "Loan {{amount}} CZK",
				lumpSumLabel: "{{amount}} CZK lump sum",
				startFrom: "From {{date}}",
				pinContract: "Pin contract",
				Actions: {
					detail: "Contract detail",
					updateValue: "Update value",
					editAllocation: "Edit allocation",
					editAllocationDescription: "Currently {{percent}} % of contract",
					unpin: "Unpin from goal",
				},
				Allocation: {
					sliderLabel: "Allocation to this goal",
					usedOnOther:
						"{{usedPercent}} % of this contract is allocated to other goals. You can assign up to {{maxPercent}} % to this goal.",
					previewTitle: "Counted toward this goal",
					valueLabel: "Value",
					outOf: "of {{amount}} CZK",
					submit: "Save allocation",
				},
				Pin: {
					chooseTitle: "Pin contract",
					chooseDescription:
						"Link the goal to a contract and see how close you are.",
					existingTitle: "Existing contract",
					existingDescription: "Pick from portfolio",
					futureTitle: "Future contract",
					futureDescription: "Your advisor will help arrange it",
					Existing: {
						title: "Select contract",
						allPinned: "All suitable contracts are already pinned.",
						hint: "Showing only contracts that can fund the goal — savings, investments, pension. Each row shows how much of its capacity is still free.",
						addNew: "Add a new contract",
						fullyAllocated: "Available capacity {{percent}} % (min {{min}} %)",
						alreadyPinned: "Already pinned ({{percent}} %) — edit",
						available: "Available {{percent}} %",
					},
					Future: {
						title: "Future contract",
						editTitle: "Edit future contract",
						nameLabel: "Contract name",
						namePlaceholder: "E.g. Pension savings",
						typeLabel: "Type",
						Types: {
							savings: "Savings / investments",
							mortgage: "Mortgage",
							loan: "Loan",
							insurance: "Insurance",
							pension: "Pension",
						},
						loanAmountLabel: "How much I'll borrow",
						loanAmountPlaceholder: "E.g. 2 000 000",
						lumpSumLabel: "Lump sum",
						lumpSumPlaceholder: "E.g. 100 000",
						monthlyLabel: "Monthly amount",
						monthlyPlaceholder: "E.g. 5 000",
						startMonthLabel: "Start date",
						noteLabel: "Note",
						notePlaceholder: "E.g. After refinancing is done",
						save: "Add future contract",
						saveEdit: "Save changes",
					},
				},
			},
			LoanCalculator: {
				titleMortgage: "Planned mortgage ({{amount}} CZK)",
				titleLoan: "Planned loan ({{amount}} CZK)",
				descriptionMortgage:
					"Once you save up the down payment, realization begins — the mortgage covers the rest.",
				descriptionLoan:
					"Once you save up the down payment, realization begins — the loan covers the rest.",
				rateLabel: "Interest rate",
				rateValue: "{{rate}} % p.a.",
				rateMin: "2 %",
				rateMax: "8 %",
				termLabel: "Repayment period",
				termValue: "{{count}} years",
				termValue_one: "{{count}} year",
				termValue_few: "{{count}} years",
				termValue_other: "{{count}} years",
				monthlyPayment: "Monthly payment",
				monthlyMeta: "at {{rate}} % p.a. · {{term}}",
				totalPaid: "Total paid",
				amountValue: "{{amount}} CZK",
				interest: "Of which interest",
				interestValue: "+{{amount}} CZK",
				disclaimer:
					"Indicative calculation. Your advisor will prepare a specific offer based on the bank and your creditworthiness.",
				showDetail: "Show detail",
				hideDetail: "Hide detail",
			},
			RentPlan: {
				title: "Rent plan",
				phaseAccumulation: "Accumulation",
				phaseDrawdown: "Drawdown",
				accumYearsLabel: "{{count}} years of saving",
				drawdownPerpetualLabel: "{{amount}}/mo forever",
				drawdownToZeroLabel: "{{years}} years · {{amount}}/mo",
				durationYears: "{{count}} years",
				accumPhaseTitle: "Now — {{year}}",
				accumPhaseDescription: "You save {{amount}} before the rent starts.",
				drawPhaseTitlePerpetual: "{{startYear}} — forever",
				drawPhaseTitleToZero: "{{startYear}} — {{endYear}}",
				drawPhaseDescriptionPerpetual:
					"You draw {{amount}} monthly from the yield. The principal stays as an inheritance.",
				drawPhaseDescriptionToZero:
					"You draw {{amount}} monthly, gradually depleting the principal to zero.",
				disclaimer: "Calculated at a real return of 4% p.a. after inflation.",
			},
			WhatIf: {
				title: "What if I added more?",
				placeholder: "Move the slider",
				talkToAdvisor: "Talk to your advisor",
				otherOptions: "Other options",
				Earlier: {
					label: "Finish sooner",
					subtitle: "{{saved}} earlier",
					today: "Today",
					earlier: "earlier",
					years: "{{count}} years",
					years_one: "{{count}} year",
					years_few: "{{count}} years",
					years_other: "{{count}} years",
					months: "{{count}} months",
					months_one: "{{count}} month",
					months_few: "{{count}} months",
					months_other: "{{count}} months",
					and: "and",
					none: "0 months",
				},
				More: {
					label: "Save more",
					subtitle: "+{{amount}} by completion",
					originalGoal: "Original goal",
					withIncrease: "With increase",
					extraToTarget: "+{{amount}} extra by completion",
				},
				Split: {
					label: "Split smartly",
					subtitle: "{{here}} here, {{there}} elsewhere",
					thisGoal: "This goal",
					redirectSurplus: "Redirect surplus",
					efficient: "You're investing more efficiently into two goals",
					noSplit: "Add more to see a split suggestion",
				},
			},
			Composition: {
				title: "Goal composition",
				ownFunds: "Own funds",
				loan: "Loan",
				savings: "Savings and investments",
				remaining: "Remaining to cover",
				coveredLabel: "covered",
			},
			Projection: {
				tileTitle: "Expected savings in {{year}}",
				missing: "Missing: {{amount}} CZK",
				warningWithCurrent:
					"You're close to the plan. Just add {{amount}} CZK/month and you'll make it by {{year}}.",
				warningNoCurrent:
					"With {{amount}} CZK/month extra, you'll make it by {{year}}.",
				offTrackWithCurrent:
					"Current {{current}} CZK/month isn't enough. You need at least {{needed}} CZK — your advisor can help find a way.",
				offTrackNoCurrent:
					"This goal has no savings yet. Start with {{amount}} CZK/month and you'll get rolling.",
				rentSource:
					"Based on rent of {{amount}} CZK/{{frequency}} over {{years}} years",
				Frequency: {
					monthly: "month",
					yearly: "year",
				},
			},
		},
		Amount: {
			millions: "M",
			thousands: "K",
			currencySign: "CZK",
		},
		Form: {
			titleCreate: "New goal",
			sectionCategory: "Category",
			sectionAbout: "About the goal",
			sectionAmount: "How much will it cost?",
			nameLabel: "Goal name",
			namePlaceholder: "E.g. My own home",
			dueDateLabel: "Target date",
			amountPlaceholder: "E.g. 500,000",
			SectionTitles: {
				Default: "Price and financing",
				Housing: "Property price and financing",
				Car: "Car price and financing",
				Retirement: "How do you want to draw your rent?",
				Education: "How much do you want?",
				Vacation: "How much do you want?",
			},
			AmountLabels: {
				Default: "Total price",
				Housing: "Property price",
				Car: "Car price",
				Retirement: "How much I'll need",
				Education: "Target amount",
				Vacation: "Target amount",
			},
			AmountHints: {
				Retirement:
					"How much will you need in retirement? Try the rent calculator, or enter the amount directly.",
				Education:
					"Tuition, supplies, accommodation — add it all up. It doesn't need to be exact.",
			},
			OwnFundsLabels: {
				Default: "Set aside",
				Housing: "Set aside",
				Car: "Set aside",
				Education: "Set aside",
				Vacation: "Set aside",
			},
			LoanLabels: {
				Default: "I want a loan",
				Housing: "I want a mortgage",
				Car: "I want a loan",
			},
			LoanHints: {
				Default:
					"We'll handle the interest rate and payments in the goal detail.",
				Housing:
					"You'll set the mortgage rate, term, and monthly payment in the goal detail.",
				Car: "We'll handle the interest rate and payments in the goal detail.",
			},
			RemainingLabels: {
				Default: "I need to save",
				Saving: "I need to save",
			},
			MonthlyConversion: {
				kicker: "Today, just save",
				heroSuffix: "CZK monthly · 4% per year",
				fomo: "Waiting costs more. If you start a year later, you'll have to invest <bold>{{extra}} CZK</bold> more each month, i.e. <bold>{{monthlyDelayed}} CZK</bold>.",
				caption: "Add the goal and start filling it today.",
			},
			Realization: {
				defaultTitle: "You don't need to save anymore",
				caption:
					"Once the goal is created, we'll guide you through the next steps.",
				Variants: {
					Home: {
						kicker: "Ready to realize",
						description:
							"You have the funds for the down payment. Let's go straight to the mortgage and property search.",
						Steps: {
							preApproval: {
								title: "Mortgage pre-approval",
								subtitle: "Arrange with your agent",
							},
							search: {
								title: "Property search",
								subtitle: "Tips and comparisons in Reality Hub",
							},
							documents: {
								title: "Mortgage documents",
								subtitle: "Income, statements, identity",
							},
						},
					},
					Car: {
						kicker: "Ready to realize",
						description:
							"You have the funds for the down payment. Let's go straight to the loan and choosing a car.",
						Steps: {
							preApproval: {
								title: "Loan pre-approval",
								subtitle: "Loan or leasing with your agent",
							},
							pick: {
								title: "Choose a car",
								subtitle: "Models, equipment, financing",
							},
							documents: {
								title: "Loan documents",
								subtitle: "Income, statements, identity",
							},
						},
					},
					Retirement: {
						kicker: "Ready for retirement",
						description:
							"You're saved up. Let's start the retirement drawdown.",
						Steps: {
							start: {
								title: "Start drawdown",
								subtitle: "Date and amount with your agent",
							},
							strategy: {
								title: "Retirement strategy",
								subtitle: "More conservative portfolio for stability",
							},
						},
					},
					Education: {
						kicker: "Ready to realize",
						description: "You're saved up. Let's go straight to education.",
						Steps: {
							enroll: {
								title: "Enrollment and payment",
								subtitle: "Arrange the date and pay",
							},
							plan: {
								title: "Plan the studies",
								subtitle: "Start and schedule",
							},
						},
					},
					Custom: {
						kicker: "Ready to realize",
						description: "You're saved up. The goal is ready to realize.",
						Steps: {
							schedule: {
								title: "Set a date",
								subtitle: "When you'll start",
							},
							tasks: {
								title: "First steps",
								subtitle: "What needs to be done",
							},
						},
					},
					Vacation: {
						kicker: "Ready to travel",
						description: "You're saved up. Let's plan the trip.",
						Steps: {
							book: {
								title: "Transport & accommodation",
								subtitle: "Trip booking",
							},
							plan: {
								title: "Insurance & documents",
								subtitle: "Travel insurance, visas",
							},
						},
					},
					Rent: {
						kicker: "Ready to draw down",
						description: "You're saved up. Let's set up the rent payouts.",
						Steps: {
							start: {
								title: "Set up drawdown",
								subtitle: "When to start, how much monthly",
							},
							strategy: {
								title: "Investment strategy",
								subtitle: "Rebalancing for the drawdown phase",
							},
						},
					},
				},
			},
			Pension: {
				sectionTitle: "How do you want to draw your rent?",
				Drawdown: {
					toZeroLabel: "Until depleted",
					perpetualLabel: "Perpetual rent",
				},
				monthlyLabel: "Monthly amount",
				monthlyPlaceholder: "E.g. 30,000",
				statePensionLabel: "Of which state pension",
				statePensionPlaceholder: "E.g. 18,000",
				yearsLabel: "For",
				yearsPlaceholder: "E.g. 20",
				yearsSuffix: "years",
				privateMonthlyLabel: "From your own funds monthly",
				privateMonthlyBreakdown: "{{desired}} wanted − {{state}} state",
				totalLabelPerpetual: "You need to have (forever)",
				totalLabelToZero: "You need to have at start of drawdown",
				totalDescriptionPerpetual:
					"You draw only the real return, principal remains as inheritance. Calculated with 4% p.a. real return after inflation.",
				totalDescriptionToZero:
					"You'll deplete the principal over {{years}} years. Calculated with 4% p.a. real return after inflation.",
				ownFundsLabel: "Set aside",
				ownFundsPlaceholder: "E.g. 500,000",
			},
			submitCreate: "Add goal",
			cancel: "Cancel",
			Categories: {
				Housing: "Housing",
				Car: "Car",
				Retirement: "Retirement",
				Rent: "Rent",
				Education: "Education",
				Vacation: "Vacation",
				Custom: "Custom",
			},
			DefaultNames: {
				Housing: "Buying a home",
				Car: "New car",
				Retirement: "For retirement",
				Rent: "Passive rent",
				Education: "Kids' education",
				Vacation: "Dream vacation",
			},
		},
	},
	Auth: {
		LoginForm: {
			offline: "You are working in offline mode.",
		},
	},
	VersionUpdate: {
		modalTitle: "New application version",
		modalContent:
			"A new application version was found. Do you want to restart the app?",
	},
};
